import React, { useState, useRef, useEffect } from 'react';
import styles from './styles.less';
import classNames from 'classnames';

import { ColorEncodingPanel } from './color-encoding.tsx';
import { SetModal } from '../set-editor/index.ts';

interface Encoding {
    type: string;
    color?: string;
}

interface SetExpr {
    type?: string;
    sets?: string[];
    gfql?: Array<{
        type?: string;
        filter_dict?: Record<string, any>;
    }>;
    dataType?: string;
}

interface Set {
    id: string;
    name?: string;
    type?: string;
    description?: string;
    node_encoding: Encoding;
    edge_encoding: Encoding;
    expr?: SetExpr;
}

interface IntersectionSetName {
    id: string;
    name?: string;
}

interface Column {
    name: string;
    type?: string;
}

interface EncodingChangeParams {
    index: string | number;
    value: string | null;
    encodingType: string;
    graphType: string;
}

interface UpdateCollectionNameParams {
    index: string | number;
    value: string;
}

interface UpdateCollectionSetsParams {
    value: Record<string | number, Set>;
    editIndex: string | number;
}

interface EditConfirmValue {
    value: Record<string, Set>;
}

interface SetItemProps {
    index: string | number;
    set: Set;
    setIcon: string;
    handleDragStart: (e: React.DragEvent<HTMLDivElement>, index: string | number) => void;
    handleDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
    handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
    handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
    handleDrop: (e: React.DragEvent<HTMLDivElement>, index: string | number) => void;
    onUpdateCollectionNodeEncoding: (params: EncodingChangeParams) => void;
    onUpdateCollectionEdgeEncoding: (params: EncodingChangeParams) => void;
    onUpdateCollectionName: (params: UpdateCollectionNameParams) => void;
    onUpdateCollectionSets: (params: UpdateCollectionSetsParams) => void;
    onDeleteCollection: (index: string | number) => void;
    isGlobal?: boolean;
    isEdgeGlobalPassthrough?: boolean;
    isAllNonGlobalEdgesPassthrough?: boolean;
    columns?: Column[];
    intersectionSetsNames?: IntersectionSetName[];
}

export const SetItem: React.FC<SetItemProps> = ({
    index,
    set,
    setIcon,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    onUpdateCollectionNodeEncoding,
    onUpdateCollectionEdgeEncoding,
    onUpdateCollectionName,
    onUpdateCollectionSets,
    onDeleteCollection,
    isGlobal = false,
    isEdgeGlobalPassthrough = false,
    isAllNonGlobalEdgesPassthrough = false,
    columns = [],
    intersectionSetsNames = []
}) => {
    const [activePanel, setActivePanel] = useState<string | null>(null);
    const [pButtonRef, setPButtonRef] = useState<HTMLButtonElement | null>(null);
    const [eButtonRef, setEButtonRef] = useState<HTMLButtonElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(set.name || `Set ${set.id}`);
    const inputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);

    const nodeColor = set.node_encoding.color;
    const edgeColor = set.edge_encoding.color;
    const isNodePassthrough = set.node_encoding.type === 'passthrough';
    const isEdgePassthrough = set.edge_encoding.type === 'passthrough';

    // click outside of panel to close it
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (activePanel &&
                panelRef.current &&
                !panelRef.current.contains(target) &&
                !pButtonRef?.contains(target) &&
                !eButtonRef?.contains(target) &&
                !target.closest(`.${styles['color-encoding-panel']}`)) {
                setActivePanel(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [activePanel, pButtonRef, eButtonRef]);

    const handlePanelToggle = (panelType: string): void => {
        setActivePanel(activePanel === panelType ? null : panelType);
    };


    const initDragStart = (e: React.DragEvent<HTMLDivElement>, index: string | number): void => {
        if (isGlobal) return; // prevent dragging
        handleDragStart(e, index);
    };

    const handleDoubleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (isGlobal) return; // prevent editing
        e.preventDefault();
        setIsEditing(true);
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setEditName(e.target.value);
    };

    const handleNameSubmit = (): void => {
        if (editName.trim() !== '') {
            onUpdateCollectionName({
                index,
                value: editName.trim()
            });
        } else {
            setEditName(set.name || `Set ${set.id}`);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            handleNameSubmit();
        } else if (e.key === 'Escape') {
            setEditName(set.name || `Set ${set.id}`);
            setIsEditing(false);
        }
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleEditSet = (): void => {
        if (isGlobal) return;
        setShowEditModal(true);
    };

    const handleEditConfirm = ({ value }: EditConfirmValue): void => {
        // The edited set from the modal
        const editedSet = Object.values(value)[0];

        // merged set with ids + encodings
        const mergedSet: Set = {
            ...set,
            ...editedSet,
            id: set.id,
            node_encoding: set.node_encoding,
            edge_encoding: set.edge_encoding,
        };

        onUpdateCollectionSets({
            value: { [index]: mergedSet },
            editIndex: index
        });

        setShowEditModal(false);
    };

    return (
        <div
            className={`${styles['set-item']} ${isGlobal ? styles['global-item'] : ''}`}
            draggable={!isEditing && !isGlobal}  // cant drag while editing or if global
            onDragStart={isGlobal ? undefined : (e) => initDragStart(e, index)}
            onDragEnd={isGlobal ? undefined : handleDragEnd}
            onDragOver={isGlobal ? undefined : handleDragOver}
            onDragLeave={isGlobal ? undefined : handleDragLeave}
            onDrop={isGlobal ? undefined : (e) => handleDrop(e, index)}
        >
            <div className={styles['drag-handle']}>
                {isGlobal ? (
                    <div className={styles['dots-grid-empty']} />
                ) : (
                    <div className={styles['dots-grid']}>
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className={styles['dot']} />
                        ))}
                    </div>
                )}
            </div>
            <div className={styles['set-info']}>
                <div className={styles['set-content-wrapper']}>
                    <div className={styles['set-name-row']}>
                        {!isGlobal && setIcon && (
                            <img src={setIcon} alt="Set icon" style={{ marginRight: '8px', width: '18px', height: '18px' }} />
                        )}
                        <div
                            className={styles['set-name']}
                            onDoubleClick={handleDoubleClick}
                            title={set.name || `Set ${set.id}`}
                        >
                            {isEditing ? (
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={editName}
                                    onChange={handleNameChange}
                                    onBlur={handleNameSubmit}
                                    onKeyDown={handleKeyDown}
                                    className={styles['set-name-input']}
                                />
                            ) : (
                                set.name || `Set ${set.id}`
                            )}
                        </div>
                    </div>
                    {set.description && (
                        <div className={styles['collection-description']}>
                            {set.description}
                        </div>
                    )}
                    {set.type === 'intersection' && set.expr?.sets && set.expr.sets.length > 0 && (
                        <div className={styles['intersection-container']}>
                            {set.expr.sets.map((setId, idx) => {
                                const intersectedSet = intersectionSetsNames.find(s => s.id === setId);
                                return (
                                    <div key={idx} className={styles['intersection-pill']}>
                                        {intersectedSet?.name || setId}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <div className={styles['set-controls']}>
                    <button
                        className={styles['control-button']}
                        ref={setPButtonRef}
                        onClick={() => handlePanelToggle('P')}
                        title="Point encoding"
                    >
                        <span>P</span>
                        <span
                            className={classNames(
                                styles['circle-indicator'],
                                isNodePassthrough && styles['passthrough']
                            )}
                            style={{backgroundColor: isNodePassthrough ? 'white' : nodeColor}}
                        />
                    </button>

                    <button
                        className={styles['control-button']}
                        ref={setEButtonRef}
                        onClick={() => handlePanelToggle('E')}
                        disabled={(isEdgeGlobalPassthrough && !isGlobal) || (isGlobal && !isAllNonGlobalEdgesPassthrough)}
                        title={(isEdgeGlobalPassthrough && !isGlobal) ? "Edge encoding disabled when global edge encoding is passthrough" :
                               (isGlobal && !isAllNonGlobalEdgesPassthrough) ? "Global edge encoding disabled until all set edge encodings are passthrough" :
                               "Edge encoding"}
                    >
                        <span>E</span>
                        <span
                            className={classNames(
                                styles['dash-indicator'],
                                isEdgePassthrough && styles['passthrough']
                            )}
                            style={{backgroundColor: isEdgePassthrough ? 'white' : edgeColor}}
                        />
                    </button>

                    <button
                        className={classNames(styles['edit-button'], (isGlobal || set.type === 'intersection') && styles['disabled'])}
                        onClick={(isGlobal || set.type === 'intersection') ? undefined : handleEditSet}
                        title={isGlobal ? "Cannot edit Global Set" : set.type === 'intersection' ? "Cannot edit Intersection Set" : "Edit Set"}
                    >
                        <i className="fa fa-pencil" />
                    </button>

                    <button
                        className={classNames(styles['delete-button'], isGlobal && styles['disabled'])}
                        onClick={isGlobal ? undefined : () => setShowDeleteConfirm(true)}
                        title={isGlobal ? "Cannot delete Global Set" : "Delete Set"}
                    >
                        <i className="fa fa-trash-o" />
                    </button>
                </div>
            </div>

            {activePanel && (
                <div ref={panelRef}>
                    {activePanel === 'P' &&
                        <ColorEncodingPanel
                            anchorEl={pButtonRef}
                            type="P"
                            collectionId={index}
                            onEncodingChange={(params) => {
                                onUpdateCollectionNodeEncoding(params);
                            }}
                            encodingType={set.node_encoding.type}
                            color={nodeColor}
                        />
                    }
                    {activePanel === 'E' &&
                        <ColorEncodingPanel
                            anchorEl={eButtonRef}
                            type="E"
                            collectionId={index}
                            onEncodingChange={(params) => {
                                onUpdateCollectionEdgeEncoding(params);
                            }}
                            encodingType={set.edge_encoding.type}
                            color={edgeColor}
                        />
                    }
                </div>
            )}

            {showEditModal && (
                <SetModal
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onConfirm={handleEditConfirm}
                    // this file's Set interface and SetModal's are structurally
                    // divergent (enabled/dataType); ts>=4 rejects what ts3.9 allowed
                    collections={{ sets: { [index]: set }, columns } as any}
                    interactionDetails={{
                        type: set.expr?.type || 'gfql_chain',
                        componentType: set.expr?.type === 'intersection' ? 'node' : (set.expr?.gfql?.[0]?.type === "Edge" ? "edge" : "node"),
                        name: set.expr?.type === 'intersection' ? '' : (Object.keys(set.expr?.gfql?.[0]?.filter_dict || {})[0] || ""),
                        value: set.expr?.type === 'intersection' ? '' : (Object.values(set.expr?.gfql?.[0]?.filter_dict || {})[0] || ""),
                        dataType: set.expr?.type === 'intersection' ? 'string' : set.expr?.dataType,
                        columns
                    } as any}
                    initialName={set.name}
                    initialDescription={set.description || ''}
                    initialGfql={set.expr?.type === 'intersection' ? '[]' : JSON.stringify(set.expr?.gfql || [], null, 2)}
                />
            )}

            {showDeleteConfirm && (
                <div className={styles['delete-confirm-modal']}>
                    <div className={styles['delete-confirm-content']}>
                        <h3>Delete Set</h3>
                        <p>Are you sure you want to delete this set?</p>
                        <div className={styles['delete-confirm-actions']}>
                            <button
                                className={styles['cancel-button']}
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles['delete-button-confirm']}
                                onClick={() => {
                                    onDeleteCollection(index);
                                    setShowDeleteConfirm(false);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
